import { useParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import { cn } from '@/lib/utils';
import { useGetQuotaStatus } from '@/reactQuery/get';
import useTranslation from '@/hooks/useTranslation';
import { QuotaOptionStatus } from '@/types';

function getBarColor(percentage: number): string {
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 80) return 'bg-yellow-500';
  return 'bg-green-500';
}

function QuotaOptionRow({ option }: { option: QuotaOptionStatus }) {
  const { lang } = useTranslation();
  const barColor = getBarColor(option.fill_percentage);
  const clampedFill = Math.min(option.fill_percentage, 100);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium truncate max-w-[200px]">{option.value}</span>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span>
            {option.current} / {option.target}
          </span>
          <span
            className={cn(
              'font-semibold min-w-[48px] text-right',
              option.is_full ? 'text-red-600' : 'text-foreground',
            )}
          >
            {option.is_full ? lang.get('msg.quotaFull') : `${Math.round(option.fill_percentage)}%`}
          </span>
        </div>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-neutral-100">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${clampedFill}%` }}
        />
      </div>
    </div>
  );
}

function QuotaTrackingWidget() {
  const { lang } = useTranslation();
  const {
    organization_id: organizationId,
    workspace_id: workspaceId,
    project_id: projectId,
  } = useParams();

  const { data, isLoading } = useGetQuotaStatus(organizationId, workspaceId, projectId);

  if (isLoading) {
    return <Skeleton className="w-full h-40 rounded-2xl" />;
  }

  if (!data?.quotas?.length) {
    return null;
  }

  return (
    <div className="flex flex-col w-full gap-6">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-bold">{lang.get('msg.quotaTrackingTitle')}</h3>
        <TooltipWrapper text={lang.get('msg.quotaTrackingTooltip')} className="max-w-[250px]">
          <Info size={16} className="text-muted-foreground hover:text-foreground transition-colors cursor-help" />
        </TooltipWrapper>
      </div>
      <div className="flex flex-col gap-6">
        {data.quotas.map((question) => (
          <div
            key={question.question_id}
            className="flex flex-col gap-3 p-5 bg-white rounded-xl border"
          >
            <h4 className="text-sm font-semibold">{question.question_text}</h4>
            <div className="flex flex-col gap-3">
              {question.options.map((option) => (
                <QuotaOptionRow key={option.value} option={option} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default QuotaTrackingWidget;
